/**
 * MyProfile — Profile-first Dashboard landing.
 *
 * The collector's digital home. Answers: who am I as a collector,
 * what have I accomplished, what am I working on, and what is
 * CollectorUp becoming?
 *
 * Purely presentational. Reuses:
 *   • LaunchTopSections   (Op WOW hero + Overview + Current Mission)
 *   • CollectorJourney via Museum? No — journey lives here now.
 *   • FutureVisionPreview + FounderClosing (Op WOW)
 *
 * Two owner actions live at the top of the identity strip:
 *   • Edit Profile     → /dashboard/collector-profile
 *   • View Public      → /c/{username}   (opens the collector's real
 *                        public profile in a new tab)
 *
 * Brand-new accounts (no albums, no certificates) receive an honest
 * "Welcome to CollectorUp" first-steps checklist instead of a wall
 * of zeros.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Trophy, ChevronRight, ExternalLink, Pencil, Plus, CheckCircle2, Circle, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import { VerifyEmailBanner } from "@/pages/collector/VerifyEmailNotice";
import {
  LaunchTopSections,
  FutureVisionPreview,
  FounderClosing,
  useDashboardSummary,
  isFoundingCollector,
} from "@/components/collector/CollectionLaunchExperience";
import CollectionAlbumSnapshotCard from "@/components/collector/CollectionAlbumSnapshotCard";

export default function MyProfile() {
  const { collector } = useCollectorAuth();
  const { data } = useDashboardSummary();

  const summary = data?.summary || {};
  const completed = data?.completed_albums || [];
  const emailVerified = Boolean(
    collector?.account_status === "active" && collector?.email_verified_at
  );

  const isBrandNew =
    (summary.albums_owned ?? 0) === 0 &&
    (summary.albums_completed ?? 0) === 0 &&
    (summary.certificates ?? 0) === 0;

  return (
    <div className="space-y-14 md:space-y-20 pb-8" data-testid="my-profile">
      <SEO title="My Profile · CollectorUp" />

      {!emailVerified && <VerifyEmailBanner email={collector?.email} />}

      {/* Owner action strip — Edit Profile + View Public Profile */}
      <OwnerActions collector={collector} />

      {/* Op WOW hero + overview + current mission — the identity core */}
      <LaunchTopSections collector={collector} />

      {isBrandNew ? (
        <NewAccountWelcome />
      ) : (
        <>
          <HallOfFameProfile completed={completed} />
          <CollectorJourney albums={(data?.completed_albums || []).concat(data?.continue_collecting || [])} />
        </>
      )}

      <FutureVisionPreview />
      <FounderClosing name={collector?.first_name || collector?.display_name} />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Owner action row — Edit + View Public Profile.
 * ────────────────────────────────────────────────────────────────*/
function OwnerActions({ collector }) {
  const username = collector?.username;
  const publicHref = username ? `/collectors/${username}` : null;
  return (
    <section
      className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-[var(--ink)] bg-white px-4 md:px-5 py-3 shadow-[4px_4px_0_var(--panini-yellow)]"
      data-testid="myprofile-owner-actions"
    >
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--smoke)]">Your collector home</div>
        <div className="font-display text-lg md:text-xl truncate">My Profile</div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/dashboard/collector-profile"
          data-testid="myprofile-edit"
          className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white px-4 py-2 text-xs font-black uppercase tracking-widest text-[var(--ink)] hover:bg-neutral-50"
        >
          <Pencil size={13} /> Edit Profile
        </Link>
        {publicHref ? (
          <a
            href={publicHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="myprofile-view-public"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white px-4 py-2 text-xs font-black uppercase tracking-widest hover:brightness-110"
          >
            <ExternalLink size={13} /> View Public Profile
          </a>
        ) : (
          <span
            className="inline-flex items-center gap-2 rounded-full border-2 border-dashed border-[var(--ink)] px-4 py-2 text-xs font-black uppercase tracking-widest text-[var(--smoke)]"
            title="Set a username in Edit Profile to enable your public profile."
            data-testid="myprofile-public-locked"
          >
            <ExternalLink size={13} /> Set username to enable
          </span>
        )}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Hall of Fame (profile-side, view-only, links to My Collection).
 * ────────────────────────────────────────────────────────────────*/
function HallOfFameProfile({ completed }) {
  return (
    <section data-testid="myprofile-hall-of-fame">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-bold text-[#8a6a1f]">
            <Trophy size={13} /> Hall of Fame
          </div>
          <h2 className="mt-2 font-display font-bold text-3xl md:text-4xl">Verified Completions</h2>
        </div>
        <Link
          to="/dashboard/my-collection"
          data-testid="myprofile-hof-manage"
          className="text-xs font-black uppercase tracking-widest text-[var(--panini-red)] hover:underline inline-flex items-center gap-1"
        >
          Manage in My Collection <ChevronRight size={13} />
        </Link>
      </div>
      {completed.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-[#c8a24d]/60 bg-white p-10 text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#fff3d6] grid place-items-center">
            <Trophy size={24} className="text-[#8a6a1f]" />
          </div>
          <h3 className="mt-4 font-display text-xl md:text-2xl">Your first trophy is waiting.</h3>
          <p className="mt-2 text-sm text-[var(--smoke)] max-w-md mx-auto">
            Complete an album and it will earn a permanent gold card here.
          </p>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2">
          {completed.slice(0, 6).map((a) => (
            <div key={a.id} data-testid={`myprofile-trophy-${a.id}`}>
              <CollectionAlbumSnapshotCard
                album={a}
                variant="trophy"
                layout="horizontal"
                showCounts={false}
                footer={
                  a.certificate_number ? (
                    <a
                      href={`/certificates/${a.certificate_number}`}
                      className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-emerald-900 hover:underline"
                      data-testid={`myprofile-trophy-certificate-${a.id}`}
                    >
                      Certificate ↗
                    </a>
                  ) : null
                }
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Collector Journey (profile) — timeline of tournaments owned.
 *
 * C3.2 corrections:
 *   1. Ownership matching no longer depends on one fragile hardcoded
 *      `public_slug` string. Any album whose canonical identifiers
 *      map to a Journey stop's tournament year (and, for editions
 *      with a distinct competition code, its `competition_code`)
 *      counts as owned. `slug` variations therefore no longer break
 *      the timeline — a Panini WC 2026 HC and SC both mark 2026.
 *   2. `isFuture` uses strict `year > currentYear` so 2026 during
 *      2026 is never labelled Coming Soon.
 *   3. Supported historical editions (2022, 2018, 2014, 2010) are
 *      part of the timeline. If the collector doesn't own them yet
 *      they render as "Not Yet Collected" — never as "Coming Soon".
 * ────────────────────────────────────────────────────────────────*/
function CollectorJourney({ albums }) {
  // Precompute lookup tables so we can match every album shape the
  // collector might hold — canonical slug, tournament year, or a
  // competition identifier — without depending on any single field.
  const albumsList = Array.isArray(albums) ? albums : [];
  const ownedYears = new Set();
  const ownedSlugs = new Set();
  const ownedCompetitions = new Set();
  for (const a of albumsList) {
    if (!a) continue;
    if (a.public_slug) ownedSlugs.add(String(a.public_slug).toLowerCase());
    if (a.slug) ownedSlugs.add(String(a.slug).toLowerCase());
    if (a.checklist_slug) ownedSlugs.add(String(a.checklist_slug).toLowerCase());
    if (a.album_slug) ownedSlugs.add(String(a.album_slug).toLowerCase());
    const year = Number(a.tournament_year || a.year);
    if (Number.isFinite(year)) ownedYears.add(year);
    if (a.competition_code) ownedCompetitions.add(String(a.competition_code).toLowerCase());
    if (a.competition) ownedCompetitions.add(String(a.competition).toLowerCase());
  }

  // `competition` — "world_cup" (default) or "womens_world_cup" so a
  // 2026 men's album never matches a 2027 women's stop.
  const stops = [
    { year: 2010, competition: "world_cup",       slugs: ["panini-wc-2010", "wc-2010", "south-africa-2010"], title: "2010 World Cup",              subtitle: "South Africa" },
    { year: 2014, competition: "world_cup",       slugs: ["panini-wc-2014", "wc-2014", "brazil-2014"],       title: "2014 World Cup",              subtitle: "Brazil" },
    { year: 2018, competition: "world_cup",       slugs: ["panini-wc-2018", "wc-2018", "russia-2018"],       title: "2018 World Cup",              subtitle: "Russia" },
    { year: 2022, competition: "world_cup",       slugs: ["panini-wc-2022", "wc-2022", "qatar-2022"],        title: "2022 World Cup",              subtitle: "Qatar" },
    { year: 2023, competition: "womens_world_cup",slugs: ["womens-2023", "wwc-2023"],                        title: "Women's World Cup 2023",       subtitle: "Australia / New Zealand" },
    { year: 2026, competition: "world_cup",       slugs: ["panini-wc-2026", "panini-wc-2026-hc", "panini-wc-2026-sc", "can-mex-usa-2026", "wc-2026"], title: "2026 World Cup",             subtitle: "CAN / MEX / USA" },
    { year: 2027, competition: "womens_world_cup",slugs: ["womens-2027", "wwc-2027"],                        title: "Women's World Cup 2027",       subtitle: "Brazil" },
    { year: 2030, competition: "world_cup",       slugs: ["world-cup-2030", "panini-wc-2030"],               title: "2030 World Cup",              subtitle: "Iberia + North Africa" },
  ];
  const currentYear = new Date().getFullYear();

  const isOwned = (s) => {
    for (const slug of s.slugs) if (ownedSlugs.has(slug)) return true;
    // Year-based fallback: an album with a matching tournament year
    // AND either no explicit competition code OR the matching one.
    for (const a of albumsList) {
      const y = Number(a?.tournament_year || a?.year);
      if (y !== s.year) continue;
      const comp = String(a?.competition_code || a?.competition || "").toLowerCase();
      if (!comp || comp === s.competition) return true;
    }
    return false;
  };

  return (
    <section data-testid="myprofile-journey">
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--panini-red)]">
          <Clock size={13} /> Story
        </div>
        <h2 className="mt-2 font-display font-bold text-3xl md:text-4xl">Your Collector Journey</h2>
        <p className="mt-3 text-sm md:text-base text-[var(--smoke)]">Every tournament, one continuous collection.</p>
      </div>
      <ol className="mt-10 relative pl-4 md:pl-0 md:mx-auto md:max-w-3xl">
        <div aria-hidden className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#c8a24d] via-[var(--stone)] to-[#0e1a2e]/40" />
        {stops.map((s, idx) => {
          const owned = isOwned(s);
          // C3.2 — strictly greater than the current year. 2026 during
          // 2026 is a present-day supported edition, never "Coming Soon".
          const isFuture = s.year > currentYear;
          const align = idx % 2 === 0 ? "left" : "right";
          return (
            <li key={s.year} className="relative py-6 md:grid md:grid-cols-2 md:gap-8 items-center" data-testid={`myprofile-journey-${s.year}`}>
              <div className={"absolute left-4 md:left-1/2 -translate-x-1/2 h-4 w-4 rounded-full border-2 " +
                (owned ? "bg-[#c8a24d] border-[#8a6a1f]" : "bg-white border-[var(--ink)]")} aria-hidden />
              <div className={"pl-10 md:pl-0 " + (align === "left" ? "md:pr-10 md:text-right" : "md:pl-10 md:col-start-2")}>
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--smoke)]">{s.year}</div>
                <div className="mt-1 font-display font-bold text-xl md:text-2xl text-[var(--ink)]">{s.title}</div>
                <div className="text-sm text-[var(--smoke)]">{s.subtitle}</div>
                <div className={"mt-2 flex items-center gap-2 " + (align === "left" ? "md:justify-end" : "")}>
                  {owned ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-900 text-white px-2.5 py-1 text-[10px] uppercase tracking-[0.24em] font-bold" data-testid={`myprofile-journey-${s.year}-status-owned`}>
                      <CheckCircle2 size={11} /> In Your Collection
                    </span>
                  ) : isFuture ? (
                    <span className="inline-flex items-center gap-1 rounded-full border-2 border-dashed border-[var(--ink)] px-2.5 py-1 text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--ink)]" data-testid={`myprofile-journey-${s.year}-status-future`}>
                      <Clock size={11} /> Coming Soon
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full border-2 border-[var(--stone)] px-2.5 py-1 text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--smoke)]" data-testid={`myprofile-journey-${s.year}-status-not-collected`}>
                      Not Yet Collected
                    </span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * New account welcome — honest first-steps checklist.
 * ────────────────────────────────────────────────────────────────*/
function NewAccountWelcome() {
  const steps = [
    { title: "Complete your profile", cta: "Edit Profile", to: "/dashboard/collector-profile" },
    { title: "Add your first album",  cta: "Add Album",   to: "/dashboard/my-collection" },
    { title: "Track your collection", cta: null,          hint: "Update progress from any album card." },
    { title: "Complete an album",     cta: null,          hint: "Cross 100% to earn a Hall of Fame trophy." },
    { title: "Earn your first certificate", cta: null,    hint: "Gil issues certificates for verified completions." },
  ];
  return (
    <section data-testid="myprofile-welcome-newacc" className="rounded-3xl border-2 border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--panini-yellow)] p-6 md:p-10">
      <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--panini-red)]">Welcome to CollectorUp</div>
      <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl">Your collector journey starts here.</h2>
      <p className="mt-3 text-sm md:text-base text-[var(--smoke)] max-w-2xl">
        You now have a permanent CollectorUp identity. Add your first album to start tracking your collection — every completion becomes a trophy in your Hall of Fame.
      </p>
      <ol className="mt-6 grid gap-3 md:grid-cols-2">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start justify-between gap-3 rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)]/40 p-4"
              data-testid={`myprofile-firststep-${i}`}>
            <div className="flex items-start gap-3">
              <Circle size={16} className="mt-1 text-[var(--ink)]" aria-hidden />
              <div>
                <div className="font-bold text-[var(--ink)]">{s.title}</div>
                {s.hint && <div className="text-xs text-[var(--smoke)] mt-1">{s.hint}</div>}
              </div>
            </div>
            {s.cta && s.to && (
              <Link to={s.to} className="shrink-0 inline-flex items-center gap-1 rounded-full bg-[var(--panini-red)] text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:brightness-110">
                <Plus size={11} /> {s.cta}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
